import { useState, useMemo } from 'react';
import './styles/form.less';
import { addRegistro, addRegistroProd, sendToSalesforceBasicPlan, sendToSalesforceComplementario, sendToSalesforceVital } from './services/QRServiceRest';
import { AlertModal } from './components/AlertModal';
import { buscarCiudades } from './services/ciudadesService';
import { ALERT_MESSAGES } from './constants/alertMessages';
import {
  PLANES,
  TIPOS_DOCUMENTO,
  SALESFORCE_CONFIG,
  FORM_CONFIG
} from './constants/formConstants';
import { APP_CONFIG } from './constants/config';


const sendToSalesforce = async (formData) => {
  try {


    const nombreParts = formData.nombreCompleto.trim().split(' ');
    const firstName = nombreParts[0] || '';
    const lastName = nombreParts.slice(1).join(' ') || nombreParts[0];

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = SALESFORCE_CONFIG.WEB_TO_LEAD_URL;
    form.target = 'salesforce-iframe';
    form.style.display = 'none';

    const fields = {
      oid: SALESFORCE_CONFIG.ORG_ID,
      retURL: window.location.href,
      first_name: firstName,
      last_name: lastName,
      '00N2G00000CpToT': formData.tipoDocumento || TIPO_DOCUMENTO.CC,
      '00N2G00000CpToL': formData.numeroDocumento,
      mobile: formData.telefono,
      email: formData.email,
      city: formData.ciudad,
      recordType: '0122G000001DdTn',
      lead_source: 'Portal Corporativo'
    };

    if (formData.mediosContacto.email) {
      fields['00N2G00000B70xl'] = '1';
    }
    if (formData.mediosContacto.celular) {
      fields['00N2G00000B70xm'] = '1';
    }
    if (formData.mediosContacto.whatsapp) {
      fields['00NUj000005e6DV'] = '1';
    }
    if (formData.autorizacion) {
      fields['00N2G00000B70xn'] = '1';
    }

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value || '';
      form.appendChild(input);
    }

    console.log('📤 Datos enviando a Salesforce:', fields);

    document.body.appendChild(form);
    form.submit();
    console.log('✓ Datos enviados a Salesforce');

    setTimeout(() => {
      document.body.removeChild(form);
    }, 2000);

    return true;
  } catch (error) {
    console.error('⚠ Error al enviar a Salesforce:', error);
    return false;
  }
};

const FORM_PLAN_TYPE = 'beneficios';

/**
 * Obtiene el plan configurado localmente para el formulario.
 * Si el valor no existe en PLANES, usa 'complementario' por defecto.
 * @returns {string} El valor del plan.
 */
const getConfiguredPlan = () => {
  const planValido = PLANES.find(p => p.value === FORM_PLAN_TYPE);
  return planValido ? FORM_PLAN_TYPE : 'beneficios';
};

function App() {
  const [formData, setFormData] = useState({
    plan: getConfiguredPlan(),
    nombreCompleto: '',
    tipoDocumento: '',
    numeroDocumento: '',
    telefono: '',
    email: '',
    ciudad: '',
    mediosContacto: {
      email: false,
      celular: false,
      whatsapp: false
    },
    autorizacion: false
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [busquedaCiudad, setBusquedaCiudad] = useState('');
  const [mostrarDropdownCiudad, setMostrarDropdownCiudad] = useState(false);
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });



  const filteredCities = useMemo(() => {
    const texto = busquedaCiudad.trim().toLowerCase();
    let ciudades = buscarCiudades('');

    if (texto) {
      ciudades = ciudades.filter((ciudad) =>
        ciudad.toLowerCase().startsWith(texto)
      );
    }

    return ciudades.sort((a, b) => a.localeCompare(b, 'es'));
  }, [busquedaCiudad]);

  /**
   * Verifica si todos los campos básicos requeridos están completos.
   * @returns {boolean} Verdadero si todos los campos básicos están llenos.
   */
  const areBasicFieldsComplete = () => {
    return (
      formData.nombreCompleto.trim() !== '' &&
      formData.tipoDocumento !== '' &&
      formData.numeroDocumento.trim() !== '' &&
      formData.ciudad !== ''
    );
  };

  /**
   * Verifica si el email es válido y puede ser usado para contacto.
   * Requiere que los campos básicos estén completos.
   * @returns {boolean} Verdadero si el email es válido para contacto.
   */
  const isEmailValid = () => {
    return areBasicFieldsComplete() && formData.email.trim() !== '' && /\S+@\S+\.\S+/.test(formData.email);
  };

  /**
   * Verifica si el teléfono es válido y puede ser usado para contacto.
   * Requiere que los campos básicos estén completos.
   * @returns {boolean} Verdadero si el teléfono es válido para contacto.
   */
  const isTelefonoValid = () => {
    return areBasicFieldsComplete() && formData.telefono.trim() !== '' && formData.telefono.length === 10;
  };

  /**
   * Verifica si el formulario completo está listo para envío.
   * @returns {boolean} Verdadero si todos los campos requeridos están llenos y al menos un método de contacto está seleccionado.
   */
  const isFormComplete = () => {
    const alMenosUnMedioContacto = formData.mediosContacto.email ||
      formData.mediosContacto.celular ||
      formData.mediosContacto.whatsapp;

    return (
      formData.nombreCompleto.trim() !== '' &&
      formData.tipoDocumento !== '' &&
      formData.numeroDocumento.trim() !== '' &&
      formData.telefono.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.ciudad !== '' &&
      formData.autorizacion === true &&
      alMenosUnMedioContacto
    );
  };


  /**
   * Maneja cambios para inputs de checkbox y radio.
   * Actualiza los datos del formulario para autorizacion o mediosContacto según corresponda.
   * @param {Event} e - El evento de cambio del input.
   */
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'autorizacion') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({
        ...prev,
        mediosContacto: {
          ...prev.mediosContacto,
          [name]: checked
        }
      }));
    }
  };

  /**
   * Maneja cambios para el select de tipoDocumento.
   * Reinicia numeroDocumento cuando tipoDocumento cambia.
   * @param {string} value - El nuevo valor para tipoDocumento.
   */
  const handleTipoDocumentoChange = (value) => {
    setFormData(prev => ({ ...prev, tipoDocumento: value, numeroDocumento: '' }));
  };

  /**
   * Maneja cambios para el input de numeroDocumento.
   * Aplica formato basado en el tipoDocumento (alfanumérico para PPT/PA, numérico para otros).
   * @param {string} value - El nuevo valor para numeroDocumento.
   */
  const handleNumeroDocumentoChange = (value) => {
    let newValue = value;
    // Pasaporte (5) y Permiso de protección temporal (14): alfanumérico
    if (formData.tipoDocumento === '5' || formData.tipoDocumento === '14') {
      newValue = value.slice(0, 36);
    } else if (formData.tipoDocumento) {
      newValue = value.replace(/\D/g, '').slice(0, 16);
    }
    setFormData(prev => ({ ...prev, numeroDocumento: newValue }));
  };

  /**
   * Maneja cambios para el input de telefono.
   * Formatea el valor para permitir solo dígitos y limita a 10 caracteres.
   * Realiza validación en tiempo real y establece mensajes de error.
   * @param {string} value - El nuevo valor para telefono.
   */
  const handleTelefonoChange = (value) => {
    const newValue = value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, telefono: newValue }));

    // Validar teléfono en tiempo real
    if (newValue.trim() !== '') {
      if (newValue.length !== 10) {
        setErrors(prev => ({ ...prev, telefono: 'Ingrese un número válido de 10 dígitos' }));
      } else {
        setErrors(prev => ({ ...prev, telefono: '' }));
      }
    }
  };

  /**
   * Maneja cambios para el input de email.
   * Actualiza los datos del formulario y realiza validación de email en tiempo real.
   * @param {string} value - El nuevo valor para email.
   */
  const handleEmailChange = (value) => {
    setFormData(prev => ({ ...prev, email: value }));

    if (value.trim() !== '') {
      if (!/\S+@\S+\.\S+/.test(value)) {
        setErrors(prev => ({ ...prev, email: 'Ingrese un correo electrónico válido' }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    }
  };

  /**
   * Maneja cambios para elementos select.
   * Llama a manejadores específicos para tipoDocumento, de lo contrario actualiza los datos del formulario.
   * Gestiona clases CSS de placeholder.
   * @param {Event} e - El evento de cambio del select.
   */
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tipoDocumento') {
      handleTipoDocumentoChange(value);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (value === '') {
      e.target.classList.add('placeholder-active');
    } else {
      e.target.classList.remove('placeholder-active');
    }
  };

  /**
   * Maneja cambios para elementos de input de texto.
   * Dirige a manejadores específicos para numeroDocumento, telefono, email, o actualiza los datos del formulario directamente.
   * @param {Event} e - El evento de cambio del input.
   */
  const handleTextInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'numeroDocumento') {
      handleNumeroDocumentoChange(value);
    } else if (name === 'telefono') {
      handleTelefonoChange(value);
    } else if (name === 'email') {
      handleEmailChange(value);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  /**
   * Manejador principal de cambios para todos los inputs del formulario.
   * Dirige el evento al sub-manejador apropiado basado en el tipo de input.
   * Limpia errores para campos que no son email o telefono.
   * @param {Event} e - El evento de cambio de cualquier input del formulario.
   */
  const handleChange = (e) => {
    const { name, type } = e.target;

    if (type === 'checkbox' || type === 'radio') {
      handleCheckboxChange(e);
    } else if (e.target.tagName === 'SELECT') {
      handleSelectChange(e);
    } else {
      handleTextInputChange(e);
    }

    if (errors[name] && name !== 'email' && name !== 'telefono') {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  /**
   * Maneja el checkbox "Seleccionar todo" para medios de contacto.
   * Establece todos los mediosContacto al estado marcado si son válidos.
   * @param {Event} e - El evento de cambio del checkbox seleccionar todo.
   */
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setFormData(prev => ({
      ...prev,
      mediosContacto: {
        email: checked && isEmailValid(),
        celular: checked && isTelefonoValid(),
        whatsapp: checked && isTelefonoValid()
      }
    }));
  };

  /**
   * Alterna un medio de contacto individual.
   * Actualiza el estado de mediosContacto y ajusta selectAll en consecuencia.
   * @param {string} medio - El medio a alternar ('email', 'celular', o 'whatsapp').
   */
  const handleToggleMedio = (medio) => {
    const newMediosContacto = {
      ...formData.mediosContacto,
      [medio]: !formData.mediosContacto[medio]
    };

    setFormData(prev => ({
      ...prev,
      mediosContacto: newMediosContacto
    }));

    // Actualizar selectAll según si todos están seleccionados o no
    const todosSeleccionados = newMediosContacto.email && newMediosContacto.celular && newMediosContacto.whatsapp;
    setSelectAll(todosSeleccionados);
  };

  /**
   * Maneja el cambio en el campo de búsqueda de ciudad.
   * 
   * - Actualiza el texto ingresado por el usuario.
   * - Muestra el dropdown de ciudades filtradas.
   * - Limpia la ciudad previamente seleccionada en el formulario.
   * - Elimina el mensaje de error asociado al campo ciudad (si existe).
   * 
   * @param {React.ChangeEvent<HTMLInputElement>} e Evento del input de búsqueda.
   */
  const handleBusquedaCiudadChange = (e) => {
    const value = e.target.value;
    setBusquedaCiudad(value);
    setMostrarDropdownCiudad(true);
    setFormData((prev) => ({
      ...prev,
      ciudad: ''
    }));

    if (errors.ciudad) {
      setErrors((prev) => ({ ...prev, ciudad: '' }));
    }
  };

  const handleCityDropdownScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;


    const llegoAlFinal = scrollTop + clientHeight >= scrollHeight - 5;


    if (llegoAlFinal && !busquedaCiudad.trim()) {
      setLimiteCiudades((prev) => prev + 4);
    }
  };

  const handleSeleccionCiudad = (ciudad) => {
    setBusquedaCiudad(ciudad);
    setMostrarDropdownCiudad(false);

    setFormData((prev) => ({
      ...prev,
      ciudad
    }));

    if (errors.ciudad) {
      setErrors((prev) => ({ ...prev, ciudad: '' }));
    }
  };

  /**
   * Valida el formulario completo y establece mensajes de error.
   * Verifica todos los campos requeridos y sus formatos.
   * @returns {boolean} Verdadero si el formulario es válido, falso en caso contrario.
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombreCompleto.trim()) newErrors.nombreCompleto = 'El nombre completo es requerido';
    if (!formData.tipoDocumento) newErrors.tipoDocumento = 'Seleccione un tipo de documento';

    // Validación de número de documento según el tipo
    if (!formData.numeroDocumento.trim()) {
      newErrors.numeroDocumento = 'El número de documento es requerido';
    } else if (formData.tipoDocumento === '5' || formData.tipoDocumento === '14') {
      // Pasaporte (5) y Permiso de protección temporal (14): alfanumérico, máximo 36 caracteres
      if (formData.numeroDocumento.length > 36) {
        newErrors.numeroDocumento = 'El documento no puede exceder 36 caracteres';
      }
    } else {
      // Otros tipos: solo números, máximo 16 caracteres
      if (!/^\d+$/.test(formData.numeroDocumento)) {
        newErrors.numeroDocumento = 'El número de documento debe contener solo números';
      } else if (formData.numeroDocumento.length > 16) {
        newErrors.numeroDocumento = 'El número de documento no puede exceder 16 dígitos';
      }
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es requerido';
    } else if (!/^\d{10}$/.test(formData.telefono.replace(/\s/g, ''))) {
      newErrors.telefono = 'Ingrese un número válido de 10 dígitos';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Ingrese un correo electrónico válido';
    }

    if (!formData.ciudad.trim()) newErrors.ciudad = 'La ciudad es requerida';
    if (!formData.autorizacion) newErrors.autorizacion = 'Debe autorizar el tratamiento de datos';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Envía los datos del formulario a SharePoint.
   * @param {Object} registroData - El objeto de datos a enviar a SharePoint.
   * @returns {boolean} Verdadero si es exitoso, falso en caso contrario.
   */
  const sendToSharePoint = async (registroData) => {
    try {
      if (APP_CONFIG.environment === 'PRU') {
        await addRegistro(FORM_CONFIG.SHAREPOINT_LIST_NAME, registroData);
      } else {
        await addRegistroProd(FORM_CONFIG.SHAREPOINT_LIST_NAME, registroData);
      }
      console.log('✓ Datos guardados en SharePoint');
      return true;
    } catch (error) {
      console.error('⚠ Error al enviar a SharePoint:', error);
      return false;
    }
  };

  /**
   * Reinicia el formulario a su estado inicial.
   * Limpia todos los datos del formulario y reinicia selectAll.
   */
  const resetForm = () => {
    setFormData({
      plan: getConfiguredPlan(),
      nombreCompleto: '',
      tipoDocumento: '',
      numeroDocumento: '',
      telefono: '',
      email: '',
      ciudad: '',
      mediosContacto: {
        email: false,
        celular: false,
        whatsapp: false
      },
      autorizacion: false
    });
    setBusquedaCiudad('');
    setMostrarDropdownCiudad(false);
    setSelectAll(false);
  };

  /**
   * Maneja el envío del formulario.
   * Valida el formulario, envía datos a SharePoint y Salesforce, y muestra mensajes de éxito/error.
   * @param {Event} e - El evento de envío.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      console.log('Formulario válido:', formData);
      setSubmitted(true);

      const registroData = {
        Title: PLANES.find(p => p.value === formData.plan)?.label || formData.plan,
        NombreCompleto: formData.nombreCompleto,
        TipoDocumento: formData.tipoDocumento,
        NumeroDocumento: formData.numeroDocumento,
        Telefono: formData.telefono,
        Email: formData.email,
        Ciudad: formData.ciudad,
        ContactoPorEmail: formData.mediosContacto.email,
        ContactoPorCelular: formData.mediosContacto.celular,
        ContactoPorWhatsApp: formData.mediosContacto.whatsapp,
        AutorizacionDatos: formData.autorizacion
      };

      // SharePoint: envío no bloqueante — su fallo no afecta el flujo principal
      sendToSharePoint(registroData).then(ok => {
        if (!ok) console.warn('⚠ SharePoint: el registro no se guardó, pero el lead fue enviado a Salesforce.');
      });

      // Salesforce: envío principal — determina el resultado para el usuario
      const salesforceSuccess = await (() => {
        switch (formData.plan) {
          case 'beneficios':
            return sendToSalesforceBasicPlan(formData);
          case 'complementario':
            return sendToSalesforceComplementario(formData);
          case 'vital':
            return sendToSalesforceVital(formData);
          default:
            return sendToSalesforceComplementario(formData);
        }
      })();

      // Esperar 4 segundos para asegurar que todos los procesos se completen
      await new Promise(resolve => setTimeout(resolve, 4000));

      // Mostrar mensaje según resultado de Salesforce (fuente primaria)
      if (salesforceSuccess) {
        setAlertModal({
          isOpen: true,
          ...ALERT_MESSAGES.success,
          onConfirm: () => {
            resetForm();
            setAlertModal({ ...alertModal, isOpen: false });
          }
        });
      } else {
        setAlertModal({
          isOpen: true,
          ...ALERT_MESSAGES.error,
          onConfirm: () => {
            setAlertModal({ ...alertModal, isOpen: false });
          }
        });
      }

      setSubmitted(false);
    }
  };

  /**
   * Retorna el texto de autorización basado en el plan seleccionado.
   * @param {string} plan - El tipo de plan ('complementario', 'vital', u otros).
   * @returns {JSX.Element} El texto de autorización como JSX.
   */
  const getAutorizacionTexto = (plan) => {
    const url = 'https://corporativo.compensar.com/proteccion-de-datos';
    if (plan === 'complementario') {
      return <>
        Sí, autorizo a <b>Compensar Caja de Compensación Familiar</b>, de acuerdo con lo dispuesto en la Ley 1581 de 2012 a tratar la información registrada en este formulario de acuerdo con lo establecido en el Manual de Protección de Datos Personales de Compensar, dispuesto en: <a href={url} target="_blank" rel="noopener noreferrer" className="autorizacion-link"><b>Ver Aquí</b></a> información que será tratada por Compensar para contactarme con el fin de brindarme información respecto a mi solicitud de afiliación al Plan Complementario Especial.
      </>;
    }
    if (plan === 'vital') {
      return <>
        Sí, autorizo a <b>Compensar Caja de Compensación Familiar</b>, de acuerdo con lo dispuesto en la Ley 1581 de 2012 a tratar la información registrada en este formulario de acuerdo con lo establecido en el Manual de Protección de Datos Personales de Compensar, dispuesto en: <a href={url} target="_blank" rel="noopener noreferrer" className="autorizacion-link"><b>Ver Aquí</b></a> información que será tratada por Compensar para contactarme con el fin de brindarme información respecto a mi solicitud de afiliación al Plan Vital.
      </>;
    }
    // 'beneficios' es Plan Beneficios en Salud
    return <>
      Sí, autorizo a <b>Compensar Caja de Compensación Familiar</b>, de acuerdo con lo dispuesto en la Ley 1581 de 2012 a tratar la información registrada en este formulario de acuerdo con lo establecido en el Manual de Protección de Datos Personales de Compensar, dispuesto en: <a href={url} target="_blank" rel="noopener noreferrer" className="autorizacion-link"><b>Ver Aquí</b></a> información que será tratada por Compensar para contactarme con el fin de brindarme información respecto a mi solicitud de afiliación al Plan Beneficios en Salud.
    </>;
  };

  return (
    <div className="simple-form-container">
      {/* Componente de alerta modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        buttonText="Aceptar"
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onButtonClick={alertModal.onConfirm}
      />

      {/* Iframe oculto para recibir la respuesta de Salesforce sin redirigir */}
      <iframe name="salesforce-iframe" style={{ display: 'none' }} title="Salesforce Response"></iframe>

      <div className="form-header">
        <h2>Asesoría de {PLANES.find(p => p.value === formData.plan).label}</h2>
        <p>Registra tu información y te contactaremos lo antes posible.</p>
      </div>

      <form onSubmit={handleSubmit} className="main-form">
        {/* Información personal */}
        <fieldset>
          <legend><h3 className="section-title"><i className="fa fa-user" /> Información personal</h3></legend>

          <div className="form-group">
            <label htmlFor="nombreCompleto">
              Nombre completo <span className="required">*</span>
            </label>
            <input
              type="text"
              id="nombreCompleto"
              name="nombreCompleto"
              value={formData.nombreCompleto}
              onChange={handleChange}
              placeholder="Escribe el nombre completo"
              className={errors.nombreCompleto ? 'error' : ''}
            />
            {errors.nombreCompleto && <span className="error-message">{errors.nombreCompleto}</span>}
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1, marginRight: 10 }}>
              <label htmlFor="tipoDocumento">
                Tipo de documento <span className="required">*</span>
              </label>
              <select
                id="tipoDocumento"
                name="tipoDocumento"
                value={formData.tipoDocumento}
                onChange={handleChange}
                className={`${errors.tipoDocumento ? 'error' : ''} ${!formData.tipoDocumento ? 'placeholder-active' : ''}`}
              >
                <option value="" disabled selected>Seleccione un tipo de documento</option>
                {TIPOS_DOCUMENTO.filter(tipo => tipo.value).map(tipo => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
              {errors.tipoDocumento && <span className="error-message">{errors.tipoDocumento}</span>}
            </div>
            <div className="form-group" style={{ flex: 1, marginLeft: 10 }}>
              <label htmlFor="numeroDocumento">
                Número de documento <span className="required">*</span>
              </label>
              <input
                type="text"
                id="numeroDocumento"
                name="numeroDocumento"
                value={formData.numeroDocumento}
                onChange={handleChange}
                placeholder="Ej: 1030123456"
                className={errors.numeroDocumento ? 'error' : ''}
              />
              {errors.numeroDocumento && <span className="error-message">{errors.numeroDocumento}</span>}
            </div>
          </div>
        </fieldset>

        {/* Información del contacto */}
        <fieldset>
          <legend><h3 className="section-title"><i className="fa fa-phone" /> Información del contacto</h3></legend>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="telefono">
                Número telefónico <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="Ej: 3004567890"
                className={errors.telefono ? 'error' : ''}
                maxLength="10"
              />
              {errors.telefono && <span className="error-message">{errors.telefono}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Correo electrónico <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Ej: correo@mail.com"
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="citySearch">
                Ciudad <span className="required">*</span>
              </label>

              <input
                type="text"
                id="citySearch"
                name="citySearch"
                value={busquedaCiudad}
                onChange={handleBusquedaCiudadChange}
                onFocus={() => setMostrarDropdownCiudad(true)}
                placeholder="Buscar ciudad"
                className={`city-input ${errors.ciudad ? 'error' : ''}`}
                autoComplete="off"
              />

              {mostrarDropdownCiudad && (
                <div className="city-dropdown">
                  {filteredCities.map((ciudad, index) => (
                    <div
                      key={`${ciudad}-${index}`}
                      onMouseDown={() => handleSeleccionCiudad(ciudad)}
                      className="city-option"
                    >
                      {ciudad}
                    </div>
                  ))}
                </div>
              )}

              {errors.ciudad && (
                <span className="error-message">{errors.ciudad}</span>
              )}
            </div>

            <div className="form-group"></div>
          </div>
        </fieldset>

        {/* Medio preferido */}
        <fieldset>
          <legend><h3 className="section-title">Medio preferido para contactarte</h3></legend>

          <div className="toggle-group">
            <label className="toggle-item">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  disabled={!isEmailValid() && !isTelefonoValid()}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="title">Seleccionar todo</span>
            </label>

            <label className="toggle-item">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="email"
                  checked={formData.mediosContacto.email}
                  onChange={() => handleToggleMedio('email')}
                  disabled={!isEmailValid()}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-text">
                <span className="title">Correo electrónico</span>
                <span className="desc">Sí, autorizo recibir mensajería por correo electrónico con información de Compensar</span>
              </span>
            </label>

            <label className="toggle-item">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="celular"
                  checked={formData.mediosContacto.celular}
                  onChange={() => handleToggleMedio('celular')}
                  disabled={!isTelefonoValid()}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-text">
                <span className="title">Celular (Llamadas-SMS)</span>
                <span className="desc">Sí, autorizo recibir llamadas y mensajes de texto con información de Compensar</span>
              </span>
            </label>

            <label className="toggle-item">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="whatsapp"
                  checked={formData.mediosContacto.whatsapp}
                  onChange={() => handleToggleMedio('whatsapp')}
                  disabled={!isTelefonoValid()}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-text">
                <span className="title">WhatsApp</span>
                <span className="desc">Sí, autorizo recibir mensajería instantánea con información de Compensar</span>
              </span>
            </label>
          </div>
        </fieldset>

        {/* Autorización */}
        <fieldset>
          <div className="form-group">
            <label className="autorizacion-checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <input
                type="checkbox"
                name="autorizacion"
                checked={formData.autorizacion}
                onChange={handleChange}
              />
              <span className="autorizacion-legal">{getAutorizacionTexto(formData.plan)}</span>
            </label>
            {errors.autorizacion && <span className="error-message">{errors.autorizacion}</span>}
          </div>
        </fieldset>

        {/* Botón de envío */}
        <div className="form-actions" style={{ justifyContent: 'center', marginTop: '0px' }}>
          <button
            type="submit"
            className="btn-primary"
            disabled={!isFormComplete() || submitted}
          >
            {submitted ? (
              <>
                <i className="fa fa-spinner fa-spin" /> Enviando...
              </>
            ) : (
              <>
                <i className="fa fa-paper-plane" /> Enviar
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default App;
